import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const BLUE = '#2B7FFF';
const BG = '#050810';
const SURFACE = '#0B1120';
const CARD = '#0F1929';
const BORDER = 'rgba(255,255,255,0.07)';
const OFF_WHITE = '#A0AABB';

const plans = [
  {
    badge: 'FOUNDING MEMBER',
    badgeColor: BLUE,
    name: 'Founding',
    priceLabel: '$199',
    priceSub: 'one-time',
    priceThen: 'then $29/mo — locked forever',
    desc: 'First 50 professionals only. One payment locks in the lowest monthly rate we\'ll ever offer.',
    cta: 'Claim Your Spot',
    ctaTo: '/founding',
    ctaPrimary: true,
    highlight: true,
    tag: 'LIMITED · 50 SPOTS',
    features: [
      '$29/mo forever after — never increases',
      'Founding Member badge on your profile',
      'Full booking system',
      'Client management & history',
      'Automated SMS & email reminders',
      'Analytics dashboard',
      'Custom profile page',
      'Personal onboarding call with Christian',
      'Priority support',
    ],
  },
  {
    badge: 'SOLO PRO',
    badgeColor: '#6b7280',
    name: 'Pro',
    priceLabel: '$59',
    priceSub: '/month',
    priceThen: 'billed monthly · cancel anytime',
    desc: 'For independent professionals ready to run their business the right way.',
    cta: 'Join Waitlist',
    ctaTo: '/founding',
    ctaPrimary: false,
    highlight: false,
    tag: null,
    features: [
      'Full booking system',
      'Client management & history',
      'Automated SMS & email reminders',
      'AI booking assistant 24/7',
      'Analytics dashboard',
      'Custom profile page',
      'Standard support',
    ],
  },
  {
    badge: 'AGENCY',
    badgeColor: '#6b7280',
    name: 'Agency',
    priceLabel: '$45',
    priceSub: '/seat/mo',
    priceThen: 'min. 3 seats · custom onboarding',
    desc: 'For shops and teams. Per-seat pricing — scale as your team grows.',
    cta: 'Join Waitlist',
    ctaTo: '/founding',
    ctaPrimary: false,
    highlight: false,
    tag: null,
    features: [
      'Everything in Pro',
      'Per-seat pricing',
      'White-label booking page',
      'Dedicated account manager',
      'Team analytics dashboard',
      'API access',
      'SLA-backed support',
    ],
  },
];

const faqs = [
  {
    q: 'What does founding member pricing mean?',
    a: 'You pay $199 once. That locks your monthly rate at $29/mo forever — even as the standard plan goes up. The $199 covers your first 6+ months and you\'re locked in for life after that.',
  },
  {
    q: 'What happens after the 50 founding spots are gone?',
    a: 'The founding campaign closes and new professionals join at the standard Solo Pro or Shop plan.',
  },
  {
    q: 'Is there a contract or commitment?',
    a: 'No contracts. All plans are month-to-month — cancel any time.',
  },
  {
    q: 'Do you take a cut of my bookings or payments?',
    a: 'Never. Flat subscription only. Your income is 100% yours.',
  },
  {
    q: 'What types of professionals can use Mane?',
    a: 'Any independent service professional — barbers, stylists, nail techs, estheticians, coaches, and more.',
  },
  {
    q: 'Can I upgrade from Solo Pro to Shop later?',
    a: 'Yes. You can upgrade any time and we\'ll prorate the difference.',
  },
];

export default function Pricing() {
  return (
    <div style={{ background: 'transparent', color: '#fff', minHeight: '100vh', fontFamily: 'Inter, -apple-system, sans-serif' }}>
      <Helmet>
        <title>Pricing — Mane</title>
        <meta name="description" content="Mane pricing for independent beauty professionals. Founding $29/mo, Solo Pro $59/mo, Agency $45/seat/mo. Zero commission. Zero platform fees." />
        <meta property="og:title" content="Mane Pricing — Zero Commission Booking Platform" />
        <meta property="og:description" content="From $29/mo. No commission. No platform fees. Online booking, CRM, AI tools, and more." />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content="https://joinmane.com/og-image-v8.png" />
      </Helmet>
      <div style={{ maxWidth: '1080px', margin: '0 auto', padding: '72px 24px 96px' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '64px' }}>
          <Link to="/" style={{ color: BLUE, fontSize: '11px', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', textDecoration: 'none' }}>
            MANE
          </Link>
          <h1 style={{ fontSize: 'clamp(36px, 6vw, 56px)', fontWeight: 900, letterSpacing: '-0.03em', margin: '20px 0 16px', lineHeight: 1.05 }}>
            Simple pricing.<br />No surprises.
          </h1>
          <p style={{ color: OFF_WHITE, fontSize: 'clamp(15px, 2vw, 18px)', lineHeight: 1.6, maxWidth: '480px', margin: '0 auto' }}>
            Flat rate. No booking fees. No percentage of your income.
          </p>
        </div>

        {/* Plans */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '20px',
          marginBottom: '80px',
          alignItems: 'stretch',
        }}>
          {plans.map((plan) => (
            <div
              key={plan.name}
              style={{
                background: plan.highlight ? SURFACE : CARD,
                border: plan.highlight ? `1px solid rgba(43,127,255,0.35)` : `1px solid ${BORDER}`,
                borderRadius: '16px',
                padding: '36px 28px',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                boxShadow: plan.highlight ? '0 0 40px rgba(43,127,255,0.08)' : 'none',
              }}
            >
              {plan.tag && (
                <div style={{
                  position: 'absolute',
                  top: '-1px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: BLUE,
                  color: '#fff',
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  padding: '5px 16px',
                  borderRadius: '0 0 8px 8px',
                  whiteSpace: 'nowrap',
                }}>
                  {plan.tag}
                </div>
              )}

              <div style={{ color: plan.badgeColor, fontSize: '10px', fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '16px' }}>
                {plan.badge}
              </div>

              <div style={{ marginBottom: '4px' }}>
                <span style={{ fontSize: '46px', fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1 }}>{plan.priceLabel}</span>
                <span style={{ color: OFF_WHITE, fontSize: '14px', marginLeft: '4px' }}>{plan.priceSub}</span>
              </div>
              <div style={{ color: OFF_WHITE, fontSize: '12px', marginBottom: '16px' }}>{plan.priceThen}</div>

              <p style={{ color: OFF_WHITE, fontSize: '14px', lineHeight: 1.6, marginBottom: '24px' }}>{plan.desc}</p>

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: '10px', flexGrow: 1 }}>
                {plan.features.map((f) => (
                  <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', fontSize: '14px', color: '#ccc' }}>
                    <span style={{ color: BLUE, marginTop: '1px', flexShrink: 0 }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>

              <Link
                to={plan.ctaTo}
                style={{
                  display: 'block',
                  textAlign: 'center',
                  padding: '14px 24px',
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: '15px',
                  textDecoration: 'none',
                  background: plan.ctaPrimary ? BLUE : 'transparent',
                  color: plan.ctaPrimary ? '#fff' : OFF_WHITE,
                  border: plan.ctaPrimary ? 'none' : `1px solid ${BORDER}`,
                  marginTop: 'auto',
                }}
              >
                {plan.cta}
              </Link>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div style={{ maxWidth: '640px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '22px', fontWeight: 800, marginBottom: '32px', textAlign: 'center', letterSpacing: '-0.01em' }}>
            Common questions
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {faqs.map((faq, i) => (
              <div key={i} style={{ paddingBottom: '24px', borderBottom: `1px solid ${BORDER}` }}>
                <p style={{ fontWeight: 700, fontSize: '15px', marginBottom: '8px' }}>{faq.q}</p>
                <p style={{ color: OFF_WHITE, fontSize: '14px', lineHeight: 1.7 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer nav */}
        <div style={{ marginTop: '56px', display: 'flex', gap: '24px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <Link to="/" style={{ color: OFF_WHITE, fontSize: '13px', textDecoration: 'none' }}>← Back to home</Link>
          <Link to="/founding" style={{ color: BLUE, fontSize: '13px', fontWeight: 600, textDecoration: 'none' }}>Claim founding spot →</Link>
          <Link to="/about" style={{ color: OFF_WHITE, fontSize: '13px', textDecoration: 'none' }}>About Mane</Link>
        </div>

      </div>
    </div>
  );
}
